import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Plus,
  Building2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  StatusBadge,
  EmptyState,
  Input,
  Select,
  Dropdown,
  DropdownItem,
  ConfirmDialog,
} from '@/components/ui';
import { ClientFormModal } from '@/components/forms';
import {
  useClients,
  useDeleteClient,
  useArchiveClient,
  useUnarchiveClient,
} from '@/hooks/useClients';
import { useAuthStore } from '@/stores/auth';
import { formatCurrency } from '@aelita/shared';
import type { Client } from '@aelita/shared';

export const Route = createFileRoute('/clients')({
  component: ClientsPage,
});

function ClientsPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [archivingClient, setArchivingClient] = useState<Client | null>(null);
  const [filters, setFilters] = useState<{ status?: string; search?: string }>({});
  const { data, isLoading } = useClients(filters);
  const deleteClient = useDeleteClient();
  const archiveClient = useArchiveClient();
  const unarchiveClient = useUnarchiveClient();
  const clients = data?.data ?? [];
  const user = useAuthStore((s) => s.user);

  // Role checks
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'pm';

  const handleDelete = async () => {
    if (!deletingClient) return;
    try {
      await deleteClient.mutateAsync(deletingClient.id);
      setDeletingClient(null);
    } catch (error) {
      console.error('Failed to delete client:', error);
    }
  };

  const handleArchive = async () => {
    if (!archivingClient) return;
    try {
      if (archivingClient.status === 'archived') {
        await unarchiveClient.mutateAsync(archivingClient.id);
      } else {
        await archiveClient.mutateAsync(archivingClient.id);
      }
      setArchivingClient(null);
    } catch (error) {
      console.error('Failed to archive/unarchive client:', error);
    }
  };

  return (
    <>
      <Header
        title="Clients"
        subtitle="Manage your client relationships"
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsCreateOpen(true)}>
            New Client
          </Button>
        }
      />
      <PageContainer>
        {/* Filters */}
        <Card className="mb-6">
          <CardBody className="flex items-center gap-4">
            <Input
              placeholder="Search clients..."
              className="max-w-xs"
              value={filters.search ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
            <Select
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'prospect', label: 'Prospect' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'churned', label: 'Churned' },
                { value: 'archived', label: 'Archived' },
              ]}
              value={filters.status ?? ''}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              className="w-40"
            />
          </CardBody>
        </Card>

        {/* Clients Table */}
        <Card>
          {isLoading ? (
            <CardBody>
              <p className="text-gray-500 dark:text-gray-400">Loading clients...</p>
            </CardBody>
          ) : clients.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No clients yet"
              description="Add your first client to get started"
              action={{
                label: 'Add Client',
                onClick: () => setIsCreateOpen(true),
              }}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Client</TableHeader>
                  <TableHeader>Industry</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Contract Value</TableHeader>
                  <TableHeader>Projects</TableHeader>
                  <TableHeader className="w-12">{''}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client: any) => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <Link
                        to="/client/$clientId"
                        params={{ clientId: client.id }}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        {client.name}
                      </Link>
                      {client.email && <p className="text-sm text-gray-500 dark:text-gray-400">{client.email}</p>}
                    </TableCell>
                    <TableCell>{client.industry ?? '-'}</TableCell>
                    <TableCell>
                      <StatusBadge status={client.status} />
                    </TableCell>
                    <TableCell>
                      {client.contractValue ? formatCurrency(Number(client.contractValue)) : '-'}
                    </TableCell>
                    <TableCell>{client._count?.projects ?? 0}</TableCell>
                    <TableCell>
                      <Dropdown
                        trigger={
                          <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                            <MoreHorizontal className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          </button>
                        }
                        align="right"
                        width="w-40"
                      >
                        <DropdownItem onClick={() => setEditingClient(client)}>
                          <div className="flex items-center gap-2">
                            <Pencil className="h-4 w-4" />
                            Edit
                          </div>
                        </DropdownItem>
                        {isManager && (
                          <DropdownItem onClick={() => setArchivingClient(client)}>
                            <div className="flex items-center gap-2">
                              {client.status === 'archived' ? (
                                <>
                                  <ArchiveRestore className="h-4 w-4" />
                                  Unarchive
                                </>
                              ) : (
                                <>
                                  <Archive className="h-4 w-4" />
                                  Archive
                                </>
                              )}
                            </div>
                          </DropdownItem>
                        )}
                        {isAdmin && (
                          <DropdownItem
                            onClick={() => setDeletingClient(client)}
                            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                          >
                            <div className="flex items-center gap-2">
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </div>
                          </DropdownItem>
                        )}
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </PageContainer>

      {/* Create Modal */}
      <ClientFormModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} client={null} />

      {/* Edit Modal */}
      <ClientFormModal
        isOpen={!!editingClient}
        onClose={() => setEditingClient(null)}
        client={editingClient}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deletingClient}
        onClose={() => setDeletingClient(null)}
        onConfirm={handleDelete}
        title="Delete Client"
        message={`Are you sure you want to permanently delete "${deletingClient?.name}"? This will also delete all contacts and documents. This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        isLoading={deleteClient.isPending}
      />

      {/* Archive Confirmation */}
      <ConfirmDialog
        isOpen={!!archivingClient}
        onClose={() => setArchivingClient(null)}
        onConfirm={handleArchive}
        title={archivingClient?.status === 'archived' ? 'Unarchive Client' : 'Archive Client'}
        message={
          archivingClient?.status === 'archived'
            ? `Are you sure you want to restore "${archivingClient?.name}"? It will be set to Active status.`
            : `Are you sure you want to archive "${archivingClient?.name}"? It will be hidden from active clients but can be restored later.`
        }
        confirmLabel={archivingClient?.status === 'archived' ? 'Unarchive' : 'Archive'}
        isLoading={archiveClient.isPending || unarchiveClient.isPending}
      />
    </>
  );
}
